/**
 * KAN-722: поле "Метка" мастера подключения: префикс из справочника (сервер
 * назначит следующую метку, подсказка "будет UZ-17"), "Без метки" или
 * "Своя метка" (прежнее текстовое поле). Кнопка "+" открывает форму нового
 * префикса, созданный префикс сразу выбирается.
 */
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { LabelPrefixForm } from '@/components/telegram/LabelPrefixForm';
import { useTelegramLabelPrefixes } from '@/hooks/useTelegramLabelPrefixes';
import { LABEL_MAX, type CommonValues } from '@/lib/telegramConnect';

export const NO_LABEL_OPTION = 'Без метки';
export const CUSTOM_LABEL_OPTION = 'Своя метка';

const NONE_VALUE = 'none';
const CUSTOM_VALUE = 'custom';
const prefixValue = (code: string): string => `p:${code}`;

type LabelPatch = Partial<
  Pick<CommonValues, 'labelMode' | 'labelPrefix' | 'label' | 'labelTouched'>
>;

interface Props {
  values: Pick<CommonValues, 'labelMode' | 'labelPrefix' | 'label'>;
  onChange: (patch: LabelPatch) => void;
  disabled?: boolean;
}

export function LabelPrefixPicker({ values, onChange, disabled }: Props): JSX.Element {
  const { data } = useTelegramLabelPrefixes();
  const prefixes = data ?? [];
  const [creating, setCreating] = useState(false);

  const options = [
    { value: NONE_VALUE, label: NO_LABEL_OPTION },
    ...prefixes.map((p) => ({
      value: prefixValue(p.code),
      label: p.next_label ? `${p.code} (будет ${p.next_label})` : p.code,
    })),
  ];
  // выбранный префикс мог ещё не прийти в список (только что создан): не теряем значение
  if (
    values.labelMode === 'prefix' &&
    values.labelPrefix &&
    !prefixes.some((p) => p.code === values.labelPrefix)
  ) {
    options.push({ value: prefixValue(values.labelPrefix), label: values.labelPrefix });
  }
  options.push({ value: CUSTOM_VALUE, label: CUSTOM_LABEL_OPTION });

  const value =
    values.labelMode === 'custom'
      ? CUSTOM_VALUE
      : values.labelMode === 'prefix' && values.labelPrefix
        ? prefixValue(values.labelPrefix)
        : NONE_VALUE;

  const select = (next: string): void => {
    if (next === CUSTOM_VALUE) onChange({ labelMode: 'custom', labelTouched: true });
    else if (next.startsWith('p:')) {
      onChange({ labelMode: 'prefix', labelPrefix: next.slice(2), labelTouched: true });
    } else onChange({ labelMode: 'none', labelPrefix: '', labelTouched: true });
  };

  return (
    <div className="space-y-1">
      <Label htmlFor="connect-label-select">Метка</Label>
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <NativeSelect
            id="connect-label-select"
            data-testid="connect-label-select"
            aria-label="Метка"
            aria-describedby="connect-label-hint"
            options={options}
            value={value}
            disabled={disabled}
            onChange={(e) => select(e.target.value)}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-10 w-10 flex-shrink-0"
          data-testid="connect-label-prefix-add"
          aria-label="Добавить префикс метки"
          title="Добавить префикс метки"
          disabled={disabled}
          onClick={() => setCreating(true)}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {creating && (
        <LabelPrefixForm
          idPrefix="connect-label-prefix"
          onCancel={() => setCreating(false)}
          onSaved={(created) => {
            setCreating(false);
            onChange({ labelMode: 'prefix', labelPrefix: created.code, labelTouched: true });
          }}
        />
      )}
      {values.labelMode === 'custom' && (
        <Input
          id="connect-label"
          data-testid="connect-label-input"
          aria-label="Своя метка"
          value={values.label}
          disabled={disabled}
          autoComplete="off"
          maxLength={LABEL_MAX}
          placeholder="Например, UZ-16"
          onChange={(e) => onChange({ label: e.target.value })}
        />
      )}
      <p id="connect-label-hint" className="text-xs text-gray-500">
        Внутреннее имя аккаунта в кабинете, клиенты его не видят. С префиксом
        номер метки назначается автоматически; своя метка до {LABEL_MAX} символов.
      </p>
    </div>
  );
}

export default LabelPrefixPicker;
